import { useT, Namespace, TranslationKey } from "@racoon/i18n";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { useContractFilePicker } from "../../hooks/useContractFilePicker";
import {
  ModalFooter,
  Button,
  Label,
  DatePicker,
  Separator,
  cn,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  NO_SPINNER,
} from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { IcBin, IcDownload, IcUndo } from "@racoon/icons";
import { isDateRangeValid, isRejectedAmountKey, toNonNegativeInteger } from "@racoon/lib";
import { downloadFile } from "../../lib/fileUtils";
import { getFileIcon } from "../../lib/fileIconUtils";
import { useJobTypeOptions } from "~/shared/hooks/useJobTypeOptions";
import { JobTypePicker } from "~/shared/ui/job-type-picker";
import {
  buildTariffRate,
  contractToFormValues,
  setJobTypeTariffAmount,
  toggleJobTypeTariff,
  type JobTypeTariff,
} from "~/shared/lib/tariff-rate";
import { useWasteOperators } from "~/shared/hooks/use-waste-operators";
import { WasteOperatorSelect } from "~/shared/ui/waste-operator-select";

export type ContractFormData = {
  /** The waste operator signing the contract; fixed once created. */
  serviceProviderId: string;
  type: ConnectProto.ContractType;
  dealNumber: string;
  name: string;
  files: File[];
  fileIdsToDelete?: bigint[];
  startDate?: Date;
  endDate?: Date;
  tariffRateByJobType?: Record<string, number>;
  rentCost?: number;
  monthlyFee?: number;
};

interface ContractModalFormProps {
  onSubmit: (data: ContractFormData) => Promise<void>;
  onCancel: () => void;
  onArchive?: () => Promise<void>;
  editingContract?: ConnectProto.Contract;
}

// Customers sign volume-based or by-norm contracts; WASTE_POLYGON is offered only
// when an existing one is open, so it still shows its type (the select is locked then).
const SIGNABLE_CONTRACT_TYPES = [
  ConnectProto.ContractType.WASTE_OPERATOR,
  ConnectProto.ContractType.BY_NORM,
];

const blockNonIntegerKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (isRejectedAmountKey(e.key)) e.preventDefault();
};

export const AddContractForm: React.FC<ContractModalFormProps> = ({
  onSubmit,
  onArchive,
  editingContract,
}) => {
  const t = useT();
  const isEditing = !!editingContract;
  const isInactive = editingContract?.status === ConnectProto.ContractStatus.INACTIVE;
  const [contractType, setContractType] = useState(ConnectProto.ContractType.WASTE_OPERATOR);
  const wasteOperators = useWasteOperators();
  const [pickedOperatorId, setPickedOperatorId] = useState("");
  const operatorId = editingContract
    ? String(editingContract.serviceProviderId)
    : pickedOperatorId || wasteOperators.defaultId;
  const [contractNumber, setContractNumber] = useState("");
  const [name, setName] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [existingFileUploads, setExistingFileUploads] = useState<ConnectProto.FileUpload[]>([]);
  const [fileIdsToDelete, setFileIdsToDelete] = useState<bigint[]>([]);
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [jobTypeTariffs, setJobTypeTariffs] = useState<JobTypeTariff[]>([]);
  const [isRentBunker, setIsRentBunker] = useState(false);
  const [rentCost, setRentCost] = useState<number | "">(0);
  const [monthlyFee, setMonthlyFee] = useState<number | "">("");
  // A BY_NORM contract is a flat monthly fee: no job-type tariffs, no bunker rent.
  const isByNorm = contractType === ConnectProto.ContractType.BY_NORM;

  // Initialize form with editing contract data
  useEffect(() => {
    if (editingContract) {
      const values = contractToFormValues(editingContract);
      setContractType(values.type);
      setMonthlyFee(values.monthlyFee);
      setContractNumber(values.dealNumber);
      setName(values.name);
      setStartDate(values.startDate);
      setEndDate(values.endDate);
      setIsRentBunker(values.isRentBunker);
      setRentCost(values.rentCost);
      setJobTypeTariffs(values.tariffs);
      setFiles([]);
      setExistingFileUploads(editingContract.fileUploads ?? []);
      setFileIdsToDelete([]);
    }
  }, [editingContract]);

  const { selectFile } = useContractFilePicker((file) => {
    setFiles((prev) => [...prev, file]);
    if (!name) setName(file.name.split(".")[0]);
  });
  const { byKey: jobTypeOptionByKey } = useJobTypeOptions(t);

  const handleToggleJobType = (tariffKey: string) =>
    setJobTypeTariffs((prev) => toggleJobTypeTariff(prev, tariffKey));

  const handleRemoveJobType = (type: string) => {
    setJobTypeTariffs((prev) => prev.filter((t) => t.type !== type));
  };

  const handleUpdateAmount = (type: string, amount: string) =>
    setJobTypeTariffs((prev) => setJobTypeTariffAmount(prev, type, amount));

  const isDateRangeInvalid = !isDateRangeValid(startDate, endDate);

  const handleSubmit = async () => {
    if (isDateRangeInvalid) return;
    await onSubmit({
      serviceProviderId: operatorId,
      type: contractType,
      dealNumber: contractNumber,
      name,
      files,
      fileIdsToDelete: fileIdsToDelete.length > 0 ? fileIdsToDelete : undefined,
      startDate,
      endDate,
      // The backend rejects tariffs on BY_NORM and a fee on any other type.
      tariffRateByJobType: isByNorm ? undefined : buildTariffRate(jobTypeTariffs),
      rentCost: !isByNorm && isRentBunker ? (rentCost === "" ? 0 : rentCost) : undefined,
      monthlyFee: isByNorm ? (monthlyFee === "" ? 0 : monthlyFee) : undefined,
    });
  };

  return (
    <>
      <div className="flex flex-col gap-4 overflow-y-auto max-h-[70svh]">
        {/* Waste operator and contract type (Figma 21476:39657) */}
        <div className="flex gap-8 p-4">
          <div className="min-w-0 flex-1">
            <WasteOperatorSelect
              id="contractWasteOperator"
              value={operatorId}
              onChange={setPickedOperatorId}
              options={wasteOperators.options}
              valueLabel={editingContract?.serviceProvider?.name}
              loading={wasteOperators.loading}
              disabled={isEditing}
            />
          </div>
          <div className="flex flex-1 flex-col gap-4">
            <div className="flex text-text-secondary font-bold text-xs flex-1">
              {t(Namespace.Customers, "modal.addContract.contractSection")}
            </div>
            <div className="flex items-center gap-4">
              <Label htmlFor="contractType" className="shrink-0">
                {t(Namespace.Customers, "modal.addContract.contractType")}
              </Label>
              <Select
                value={String(contractType)}
                onValueChange={(v) => setContractType(Number(v) as ConnectProto.ContractType)}
                disabled={isEditing}
              >
                <SelectTrigger id="contractType" className="h-8 flex-1 px-2 rounded-lg bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(SIGNABLE_CONTRACT_TYPES.includes(contractType)
                    ? SIGNABLE_CONTRACT_TYPES
                    : [...SIGNABLE_CONTRACT_TYPES, contractType]
                  ).map((type) => (
                    <SelectItem key={type} value={String(type)}>
                      {t(
                        Namespace.Enum,
                        `CONTRACT_TYPE_${ConnectProto.ContractType[type]}` as TranslationKey,
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <Separator />

        {/* Number, Name and Dates */}
        <div className="flex flex-col p-4 gap-4">
          <div className="flex text-text-secondary font-bold text-xs flex-1">
            {t(Namespace.Customers, "modal.addContract.contractTerms")}
          </div>
          <div className="flex gap-2 flex-1">
            <div className="flex-[0.1] flex items-center h-8 px-2 border rounded-lg">
              <span className="text-brand-blue text-sm font-medium whitespace-nowrap">№</span>
              <input
                id="contractNumber"
                type="text"
                value={contractNumber}
                onChange={(e) => setContractNumber(e.target.value)}
                placeholder=""
                className="w-12 h-full ml-2 text-sm bg-transparent border-none outline-none"
              />
            </div>
            <div className="flex flex-1">
              <input
                id="contractName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t(
                  Namespace.Customers,
                  isByNorm
                    ? "modal.addContract.namePlaceholder.byNorm"
                    : "modal.addContract.namePlaceholder.wasteOperator",
                  { date: format(startDate ?? new Date(), "dd.MM.yyyy") },
                )}
                className="w-full text-sm h-8 px-2 border rounded-lg outline-none"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-4">
              <Label>{t(Namespace.Customers, "modal.addContract.startDate")}:</Label>
              <DatePicker
                value={startDate}
                onChange={setStartDate}
                maxDate={endDate}
                className="flex w-36 bg-white items-center rounded-lg gap-1 max-h-8 !px-2 !py-2"
                placeholder={t(Namespace.Customers, "modal.addContract.selectDate")}
              />
            </div>
            <div className="flex items-center gap-4">
              <Label>{t(Namespace.Customers, "modal.addContract.endDate")}:</Label>
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                minDate={startDate}
                className="flex w-36 bg-white items-center rounded-lg gap-1 max-h-8 !px-2 !py-2"
                placeholder={t(Namespace.Customers, "modal.addContract.selectDate")}
              />
            </div>
          </div>
          {isDateRangeInvalid && (
            <p className="text-xs text-brand-red">{t(Namespace.Validation, "dateRangeInvalid")}</p>
          )}
        </div>

        <Separator />

        {/* Job Type Tariffs Section */}
        <div className="flex flex-col p-4 gap-4">
          <div className="flex items-center justify-between gap-4">
            <div className="text-text-secondary font-bold text-xs">
              {t(Namespace.Customers, "modal.addContract.tariffs")}
            </div>
            {!isByNorm && (
              <JobTypePicker
                tariffs={jobTypeTariffs}
                onToggle={handleToggleJobType}
                inputId="contract-job-type-search"
              />
            )}
          </div>

          {isByNorm && (
            <div className="flex items-center gap-4">
              <Label htmlFor="monthlyFee" className="w-36 shrink-0">
                {t(Namespace.Customers, "modal.addContract.monthlyFee")}
              </Label>
              <div className="w-52 flex items-center justify-between h-8 px-2 border border-gray-200 rounded-lg bg-white">
                <input
                  id="monthlyFee"
                  type="number"
                  step="1"
                  min="0"
                  value={monthlyFee}
                  onKeyDown={blockNonIntegerKeys}
                  onChange={(e) => setMonthlyFee(toNonNegativeInteger(e.target.value))}
                  placeholder="0"
                  className={cn(
                    "flex-1 w-full min-w-0 h-full text-sm text-text-primary bg-transparent border-none outline-none",
                    NO_SPINNER,
                  )}
                />
                <span className="text-sm text-text-secondary whitespace-nowrap ml-2">
                  {t(Namespace.Customers, "modal.addContract.currency")}
                </span>
              </div>
            </div>
          )}

          {!isByNorm &&
            jobTypeTariffs.map((tariff) => {
              const option = jobTypeOptionByKey.get(tariff.type);
              return (
                <div key={tariff.type} className="flex flex-1 gap-3 items-center justify-between">
                  <div className="flex flex-1 min-w-0 items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {option?.label ?? t(Namespace.Enum, tariff.type as TranslationKey)}
                    </p>
                    {option?.category && (
                      <span className="shrink-0 text-xs font-medium text-brand-blue">
                        {option.category}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-1 items-center gap-2 min-w-0">
                    <div className="flex-1 flex items-center justify-between h-8 px-2 border border-gray-200 rounded bg-white min-w-0">
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={tariff.amount}
                        onKeyDown={blockNonIntegerKeys}
                        onChange={(e) => handleUpdateAmount(tariff.type, e.target.value)}
                        placeholder="0"
                        className={cn(
                          "flex-1 w-full min-w-0 h-full text-sm text-text-primary text-start bg-transparent border-none outline-none",
                          NO_SPINNER,
                        )}
                      />
                      <span className="text-sm text-text-secondary whitespace-nowrap ml-2">
                        {t(Namespace.Customers, "modal.addContract.currency")}
                      </span>
                    </div>
                    <Button
                      size="icon"
                      className="!w-10 !h-8"
                      variant="opacityRedWithoutOutline"
                      onClick={() => handleRemoveJobType(tariff.type)}
                    >
                      <IcBin className="w-4 h-4 text-brand-red" />
                    </Button>
                  </div>
                </div>
              );
            })}
        </div>

        {!isByNorm && (
          <>
            <Separator />

            <div className="flex flex-col p-4 gap-4">
              <div className="flex text-text-secondary font-bold text-xs flex-1">
                {t(Namespace.Customers, "modal.addContract.rentInfo")}
              </div>

              <div className="flex items-center gap-4">
                <div className="flex flex-1 items-center gap-2">
                  <Checkbox
                    id="rentBunkerCheckbox"
                    checked={isRentBunker}
                    className="!rounded-full data-[state=unchecked]:!bg-[#D9D9D9] !border-[#0000001A] data-[state=checked]:!bg-brand-blue"
                    onCheckedChange={(checked) => {
                      const isChecked = checked === true;
                      setIsRentBunker(isChecked);
                      if (!isChecked) {
                        setRentCost(0);
                      }
                    }}
                  />
                  <Label
                    htmlFor="rentBunkerCheckbox"
                    className="text-sm font-medium cursor-pointer"
                  >
                    {t(Namespace.Customers, "modal.addContract.bunkerRent")}
                  </Label>
                </div>

                <div
                  className={cn(
                    "flex items-center flex-1 justify-between h-8 border rounded bg-white transition-opacity w-32",
                    !isRentBunker ? "opacity-50 border-gray-200" : "border-gray-200",
                  )}
                >
                  <div className="flex flex-1 items-center gap-2 px-2 h-full">
                    <input
                      type="number"
                      step="1"
                      min="0"
                      value={rentCost}
                      onKeyDown={blockNonIntegerKeys}
                      onChange={(e) => setRentCost(toNonNegativeInteger(e.target.value))}
                      disabled={!isRentBunker}
                      placeholder="0"
                      className={cn(
                        "flex-1 w-full min-w-0 h-full text-sm text-text-primary bg-transparent border-none outline-none disabled:bg-transparent",
                        NO_SPINNER,
                      )}
                    />
                    <span className="text-sm text-text-secondary whitespace-nowrap">
                      {t(Namespace.Customers, "onboarding.contract.rentUnit")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <Separator />

        <div className="flex flex-col p-4 gap-3">
          <div className="flex text-text-secondary font-bold text-xs flex-1">
            {t(Namespace.Customers, "modal.addContract.contractFileLabel")}
          </div>

          {existingFileUploads.length === 0 && files.length === 0 && (
            <p className="text-sm font-medium text-text-primary">
              {t(Namespace.Customers, "modal.addContract.contractFileNotUploaded")}
            </p>
          )}

          {existingFileUploads.map((upload) => {
            const filename = upload.name || upload.url.split("/").pop() || "contract";
            const FileIcon = getFileIcon(filename);
            return (
              <div key={String(upload.id)} className="flex items-center justify-between gap-2">
                <div className="flex gap-1 items-center min-w-0">
                  {FileIcon && <FileIcon className="w-6 h-6 text-brand-blue shrink-0" />}
                  <span className="text-sm truncate max-w-[160px]" title={filename}>
                    {filename}
                  </span>
                  <button
                    type="button"
                    aria-label={t(Namespace.Common, "download")}
                    title={t(Namespace.Common, "download")}
                    onClick={() => downloadFile(upload.url, filename)}
                    className="text-brand-blue/70 hover:text-brand-blue transition-colors"
                  >
                    <IcDownload className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {fileIdsToDelete.includes(upload.id) ? (
                    <Button
                      size="icon"
                      className="!w-10 !h-8"
                      variant="opacityGreenWithoutOutline"
                      onClick={() => {
                        setFileIdsToDelete((prev) => prev.filter((id) => id !== upload.id));
                      }}
                    >
                      <IcUndo className="w-4 h-4 text-brand-green" />
                    </Button>
                  ) : (
                    <Button
                      size="icon"
                      className="!w-10 !h-8"
                      variant="opacityRedWithoutOutline"
                      onClick={() => {
                        setFileIdsToDelete((prev) => [...prev, upload.id]);
                      }}
                    >
                      <IcBin className="w-4 h-4 text-brand-red" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}

          {files.length > 0 &&
            files.map((file, index) => {
              const FileIcon = getFileIcon(file.name);
              return (
                <div className="flex items-center justify-between gap-2" key={index}>
                  <div className="flex gap-1 items-center min-w-0">
                    {FileIcon && <FileIcon className="w-6 h-6 text-brand-blue shrink-0" />}
                    <span className="text-sm truncate max-w-[160px]" title={file.name}>
                      {file.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="icon"
                      className="!w-10 !h-8"
                      variant="opacityRedWithoutOutline"
                      onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                    >
                      <IcBin className="w-4 h-4 text-brand-red" />
                    </Button>
                  </div>
                </div>
              );
            })}

          <button
            type="button"
            onClick={() => selectFile()}
            className="self-start text-brand-blue/90 hover:text-brand-blue text-sm font-medium whitespace-nowrap transition-opacity"
          >
            {t(Namespace.Customers, "modal.addContract.uploadDocument")}
          </button>
        </div>
      </div>

      <ModalFooter className={cn("flex justify-end", isEditing && "justify-between")}>
        {!isInactive && isEditing && (
          <Button
            id="add-contract-archive-button"
            variant="opacityRedWithoutOutline"
            onClick={onArchive}
            disabled={!isEditing}
          >
            {t(Namespace.Customers, "card.contracts.archive")}
          </Button>
        )}
        {isInactive && (
          <Button
            id="add-contract-archive-button"
            variant="opacityGreenWithoutOutline"
            onClick={onArchive}
            disabled={!isEditing}
          >
            {t(Namespace.Customers, "card.contracts.restore")}
          </Button>
        )}
        <Button
          id="add-contract-submit-button"
          variant="opacityBlueWithOutline"
          onClick={handleSubmit}
          disabled={!name.trim() || isDateRangeInvalid}
        >
          {isEditing
            ? t(Namespace.Customers, "card.save")
            : t(Namespace.Customers, "modal.addContract.create")}
        </Button>
      </ModalFooter>
    </>
  );
};
